import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { openWhatsAppHandoff } from '../../utils/studioNotifyChannels';
import CountryCodeSelect from './CountryCodeSelect';
import './ClientGuidesManagement.css';

export type StudioGuide = {
  id: string;
  title: string;
  bytes?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('The PDF could not be read.'));
    reader.readAsDataURL(file);
  });
}

function formatBytes(value?: number) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ClientGuidesManagement: React.FC = () => {
  const [guides, setGuides] = useState<StudioGuide[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState('');
  const [editingTitle, setEditingTitle] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [emailTo, setEmailTo] = useState('');
  const [emailName, setEmailName] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [waCountry, setWaCountry] = useState('+27');
  const [waPhone, setWaPhone] = useState('');
  const [waName, setWaName] = useState('');
  const [waNote, setWaNote] = useState('');
  const [whatsAppBusy, setWhatsAppBusy] = useState(false);

  const busy = saving || emailing || whatsAppBusy;

  const loadGuides = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides`, { credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The guides could not be loaded.');
      const rows: StudioGuide[] = Array.isArray(body.guides) ? body.guides : [];
      setGuides(rows);
      setSelectedIds((current) => current.filter((id) => rows.some((row) => row.id === id)));
    } catch (loadError) {
      setGuides([]);
      setError(loadError instanceof Error ? loadError.message : 'The guides could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGuides();
  }, [loadGuides]);

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : current.concat([id])
    ));
  };

  const onUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError('Choose a PDF to upload.');
      return;
    }
    if (!title.trim()) {
      setError('Give the guide a title.');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      setError('Only a PDF can be uploaded.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const contentBase64 = await readFileAsBase64(file);
      const response = await fetch(`${API_BASE_URL}/studio/guides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: title.trim(), contentBase64 }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The guide could not be uploaded.');
      setTitle('');
      setFile(null);
      setMessage(`Saved “${body.title || title.trim()}”.`);
      await loadGuides();
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'The guide could not be uploaded.');
    } finally {
      setSaving(false);
    }
  };

  const onRename = async (id: string) => {
    if (!editingTitle.trim()) {
      setError('Give the guide a title.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The title could not be saved.');
      setEditingId('');
      setEditingTitle('');
      setMessage(`Renamed to “${body.title || editingTitle.trim()}”.`);
      await loadGuides();
    } catch (renameError) {
      setError(renameError instanceof Error ? renameError.message : 'The title could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (guide: StudioGuide) => {
    if (!window.confirm(`Remove “${guide.title}”? Quotations that listed it will stop showing it.`)) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides/${guide.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The guide could not be removed.');
      setMessage(`Removed “${guide.title}”.`);
      await loadGuides();
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : 'The guide could not be removed.');
    } finally {
      setSaving(false);
    }
  };

  const onEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedIds.length) {
      setError('Choose at least one guide to email.');
      return;
    }
    if (!emailTo.trim()) {
      setError('Enter the client email address.');
      return;
    }
    setEmailing(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to: emailTo.trim(),
          clientName: emailName.trim(),
          note: emailNote.trim(),
          guideIds: selectedIds,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The guides could not be emailed.');
      if (body.status === 'sent') {
        setMessage(`Sent to ${body.destination}. The email provider accepted it. Delivery is not confirmed.`);
      } else if (body.status === 'captured') {
        setMessage('Email is not configured, so nothing was sent. Configure studio mail, or download the PDFs and send them yourself.');
      } else {
        throw new Error(body.error || 'The guides could not be emailed.');
      }
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'The guides could not be emailed.');
    } finally {
      setEmailing(false);
    }
  };

  const onWhatsApp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedIds.length) {
      setError('Choose at least one guide to send on WhatsApp.');
      return;
    }
    if (!waPhone.trim()) {
      setError('Enter the client WhatsApp number.');
      return;
    }
    setWhatsAppBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides/whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          countryCode: waCountry,
          phone: waPhone.trim(),
          clientName: waName.trim() || emailName.trim(),
          note: waNote.trim(),
          guideIds: selectedIds,
          publicOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'WhatsApp could not be prepared.');
      if (!body.whatsAppUrl) throw new Error('WhatsApp could not be prepared.');
      const handoff = openWhatsAppHandoff(body.whatsAppUrl);
      setMessage(
        handoff.opened
          ? 'WhatsApp opened with guide links. Confirm sending in WhatsApp. Links stay open for about 30 days.'
          : handoff.message,
      );
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'WhatsApp could not be prepared.');
    } finally {
      setWhatsAppBusy(false);
    }
  };

  const selectedTitles = selectedIds
    .map((id) => guides.find((guide) => guide.id === id)?.title)
    .filter(Boolean);

  return (
    <div className="client-guides" data-testid="client-guides">
      <header className="client-guides-intro">
        <h2>Client guides</h2>
        <p>
          Upload PDF style guides and other notes. Include them on a quotation, or send one or more
          by email or WhatsApp without a quotation.
        </p>
      </header>

      <form className="client-guides-upload" onSubmit={onUpload}>
        <div className="form-section">
          <label htmlFor="guide-title">Title</label>
          <input
            id="guide-title"
            className="form-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Style guide"
            maxLength={120}
            disabled={busy}
          />
        </div>
        <div className="form-section">
          <label htmlFor="guide-file">PDF</label>
          <input
            id="guide-file"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(event) => setFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
            disabled={busy}
          />
          <small className="form-help">PDF only, up to 20 MB.</small>
        </div>
        <button type="submit" className="btn-primary" disabled={busy || !file || !title.trim()}>
          {saving ? 'Saving…' : 'Upload guide'}
        </button>
      </form>

      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {message ? <p className="client-guides-note" role="status">{message}</p> : null}
      {loading ? <p className="client-guides-note">Loading guides…</p> : null}

      {!loading && guides.length === 0 ? (
        <p className="client-guides-empty">No guides yet. Upload a PDF to begin.</p>
      ) : (
        <>
          <ul className="client-guides-list">
            {guides.map((guide) => (
              <li key={guide.id} className="client-guides-item">
                {editingId === guide.id ? (
                  <div className="client-guides-edit">
                    <input
                      className="form-input"
                      value={editingTitle}
                      onChange={(event) => setEditingTitle(event.target.value)}
                      maxLength={120}
                      aria-label="Guide title"
                      disabled={busy}
                    />
                    <button type="button" className="btn-primary btn-sm" onClick={() => onRename(guide.id)} disabled={busy}>
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => { setEditingId(''); setEditingTitle(''); }}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <label className="client-guides-select">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(guide.id)}
                        onChange={() => toggleSelected(guide.id)}
                        disabled={busy}
                        aria-label={`Select ${guide.title}`}
                      />
                      <span>
                        <strong>{guide.title}</strong>
                        <span className="client-guides-meta">{formatBytes(guide.bytes)}</span>
                      </span>
                    </label>
                    <div className="client-guides-actions">
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => { setEditingId(guide.id); setEditingTitle(guide.title); }}
                        disabled={busy}
                      >
                        Rename
                      </button>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => onRemove(guide)} disabled={busy}>
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>

          <p className="client-guides-note" data-testid="client-guides-selection">
            {selectedTitles.length
              ? `Selected: ${selectedTitles.join(', ')}`
              : 'Select one or more guides above. No quotation is needed.'}
          </p>

          <form className="client-guides-email" onSubmit={onEmail} data-testid="client-guides-email">
            <h3>Email selected guides</h3>
            <div className="form-section">
              <label htmlFor="guide-email-to">Client email</label>
              <input
                id="guide-email-to"
                className="form-input"
                type="email"
                value={emailTo}
                onChange={(event) => setEmailTo(event.target.value)}
                placeholder="client@example.com"
                disabled={busy}
                required
              />
            </div>
            <div className="form-section">
              <label htmlFor="guide-email-name">Client name (optional)</label>
              <input
                id="guide-email-name"
                className="form-input"
                value={emailName}
                onChange={(event) => setEmailName(event.target.value)}
                placeholder="First name or full name"
                maxLength={200}
                disabled={busy}
              />
            </div>
            <div className="form-section">
              <label htmlFor="guide-email-note">Short note (optional)</label>
              <textarea
                id="guide-email-note"
                className="form-input"
                value={emailNote}
                onChange={(event) => setEmailNote(event.target.value)}
                rows={3}
                maxLength={1000}
                disabled={busy}
                placeholder="A line or two that appears above the signature."
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={busy || !selectedIds.length || !emailTo.trim()}
            >
              {emailing ? 'Sending…' : 'Email guides'}
            </button>
          </form>

          <form className="client-guides-whatsapp" onSubmit={onWhatsApp} data-testid="client-guides-whatsapp">
            <h3>WhatsApp selected guides</h3>
            <p className="client-guides-note">
              WhatsApp opens a message with private links to the PDFs. Files are not attached in the chat.
            </p>
            <div className="form-section client-guides-phone-row">
              <div>
                <label htmlFor="guide-wa-country">Country</label>
                <CountryCodeSelect id="guide-wa-country" value={waCountry} onChange={setWaCountry} />
              </div>
              <div>
                <label htmlFor="guide-wa-phone">Mobile / WhatsApp</label>
                <input
                  id="guide-wa-phone"
                  className="form-input"
                  type="tel"
                  value={waPhone}
                  onChange={(event) => setWaPhone(event.target.value)}
                  placeholder="82 123 4567"
                  disabled={busy}
                  required
                />
              </div>
            </div>
            <div className="form-section">
              <label htmlFor="guide-wa-name">Client name (optional)</label>
              <input
                id="guide-wa-name"
                className="form-input"
                value={waName}
                onChange={(event) => setWaName(event.target.value)}
                placeholder="First name or full name"
                maxLength={200}
                disabled={busy}
              />
            </div>
            <div className="form-section">
              <label htmlFor="guide-wa-note">Short note (optional)</label>
              <textarea
                id="guide-wa-note"
                className="form-input"
                value={waNote}
                onChange={(event) => setWaNote(event.target.value)}
                rows={3}
                maxLength={1000}
                disabled={busy}
                placeholder="A line or two under the guide links."
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={busy || !selectedIds.length || !waPhone.trim()}
            >
              {whatsAppBusy ? 'Preparing…' : 'Open WhatsApp'}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ClientGuidesManagement;
